import { ChangeDetectionStrategy, Component, effect, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggle, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { BoolEditor, MatColor } from '../../models/commons.enum';

/**
 * Editor Custom AgGrid for Booleans Input.
 * Params:
 * - color: (Optional) it allows to specify witch standard material color  to apply  'PRIMARY' 'ACCENT', 'WARN'.
 * - editor (Default true) it allows to specify witch material component to show, 'CHECKBOX' or 'SLIDETOGGLE.
 * - required: (Optional) It allows to specify required validator.
 */

const requiredBool = (c: AbstractControl<boolean | null>) => (c.value === null ? { required: true } : null);
export interface IBooleanCellRendererParams {
    color?: MatColor;
    editor?: BoolEditor;
    isEditor?: boolean;
    required?: boolean;
    disabled?: boolean;
}
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-boolean-cell-renderer',
    templateUrl: './boolean-cell-renderer.component.html',
    styleUrls: ['./boolean-cell-renderer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatCheckboxModule, MatSlideToggleModule, ReactiveFormsModule],
})
export class BooleanCellRendererComponent implements ICellRendererAngularComp {
    /**
     * (Optional) it allows to specify witch standard material color  to apply  'PRIMARY' 'ACCENT', 'WARN'.
     */
    readonly params = signal<(ICellRendererParams & IBooleanCellRendererParams) | null>(null);
    readonly color = signal<string>('');
    readonly editor = signal<BoolEditor>(BoolEditor.CHECKBOX);
    readonly disabled = signal<boolean>(false);
    readonly isEditor = signal<boolean>(false); /**  @TODO a cosa serve? */
    readonly slideToggle = viewChild<MatSlideToggle>('slideToggle');
    readonly checkbox = viewChild<MatCheckbox>('checkbox');
    readonly formControl = new FormControl<boolean | null>(null);
    readonly valueChanges = toSignal(this.formControl.valueChanges, { initialValue: null });
    private lastCommitted: boolean | null | undefined = undefined;

    constructor() {
        effect(() => {
            const value = this.valueChanges();
            const disabled = this.disabled();
            const params = this.params();
            if (!params || disabled) return;
            if (params.required && value === null) return;
            if (this.lastCommitted === value) return;
            this.lastCommitted = value;
            const field = params.colDef?.field ?? params.column?.getColId() ?? '';
            params.node.setDataValue(field, value);
        });
    }

    agInit(params: ICellRendererParams & IBooleanCellRendererParams): void {
        this.params.set(params);
        this.color.set((params.color ?? MatColor.PRIMARY).toLowerCase());
        this.editor.set(params.editor ?? BoolEditor.CHECKBOX);
        this.isEditor.set(!!params.isEditor);
        const isRequired = !!params.required;
        const disabled = !!params.disabled;
        this.disabled.set(disabled);
        if (disabled) this.formControl.disable({ emitEvent: false });
        else this.formControl.enable({ emitEvent: false });
        if (isRequired) this.formControl.addValidators(requiredBool);
        this.formControl.setValue(params.value ?? null, { emitEvent: false });
        this.formControl.updateValueAndValidity({ emitEvent: false });
    }

    refresh(params: ICellRendererParams): boolean {
        const checked = params.value;
        this.formControl.setValue(checked ?? null, { emitEvent: false });
        this.formControl.updateValueAndValidity({ emitEvent: false });
        return true;
    }
}
